<template>
  <div
    :class="[
      type === 'textarea' ? 'el-textarea' : 'el-input',
      inputSize ? 'el-input--' + inputSize : '',
      {
        'is-disabled': inputDisabled,
        'is-exceed': inputExceed,
        'el-input-group': $slots.prepend || $slots.append,
        'el-input-group--append': $slots.append,
        'el-input-group--prepend': $slots.prepend,
        'el-input--prefix': $slots.prefix || prefixIcon,
        'el-input--suffix': $slots.suffix || suffixIcon || clearable || showPassword,
        'el-input--with-state-icon': stateIcon && stateIconSrc,
        'el-input--with-arrow': hasArrow,
      }
    ]"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <template v-if="type !== 'textarea'">
      <div
        class="el-input-group__prepend"
        v-if="$slots.prepend"
      >
        <slot name="prepend" />
      </div>
      <input
        :tabindex="tabindex"
        v-if="type !== 'textarea'"
        class="el-input__inner"
        v-bind="$attrs"
        :type="showPassword ? (passwordVisible ? 'text': 'password') : type"
        :disabled="inputDisabled"
        :readonly="readonly"
        :autocomplete="autoComplete || autocomplete"
        ref="input"
        @compositionstart="handleCompositionStart"
        @compositionupdate="handleCompositionUpdate"
        @compositionend="handleCompositionEnd"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        :aria-label="label"
      >

      <span
        class="el-input__prefix"
        v-if="$slots.prefix || prefixIcon"
      >
        <slot name="prefix" />
        <i
          class="el-input__icon"
          v-if="prefixIcon"
          :class="prefixIcon"
        />
      </span>

      <span
        class="el-input__suffix"
        v-if="getSuffixVisible()"
      >
        <span class="el-input__suffix-inner">
          <template v-if="!showClear || !showPwdVisible || !isWordLimitVisible">
            <slot name="suffix" />
            <i
              class="el-input__icon"
              v-if="suffixIcon"
              :class="suffixIcon"
            />
          </template>
          <i
            v-if="showClear"
            class="el-input__icon el-icon-circle-close el-input__clear"
            @mousedown.prevent
            @click="clear"
          />
          <i
            v-if="showPwdVisible"
            class="el-input__icon el-icon-view el-input__clear"
            @click="handlePasswordVisible"
          />
          <span
            v-if="isWordLimitVisible"
            class="el-input__count"
          >
            <span class="el-input__count-inner">
              {{ textLength }}/{{ upperLimit }}
            </span>
          </span>
        </span>
        <i
          class="el-input__icon"
          v-if="validateState"
          :class="['el-input__validateIcon', validateIcon]"
        />
      </span>

      <span
        class="el-input__suffix"
        v-if="(stateIcon && stateIconSrc) || hasArrow"
      >
        <span class="el-input__suffix-inner el-input__suffix-inner--no-events">
          <transition
            name="icon-fade"
            mode="out-in"
          >
            <img
              :class="`input-state-icon ${stateIcon}`"
              :key="stateIcon"
              :src="stateIconSrc"
            >
          </transition>
          <i
            v-if="hasArrow"
            class="el-input__arrow icon icon-chevron-up-small"
          />
        </span>
      </span>

      <div
        class="el-input-group__append"
        v-if="$slots.append"
      >
        <slot name="append" />
      </div>
    </template>
    <textarea
      v-else
      :tabindex="tabindex"
      class="el-textarea__inner"
      @compositionstart="handleCompositionStart"
      @compositionupdate="handleCompositionUpdate"
      @compositionend="handleCompositionEnd"
      @input="handleInput"
      ref="textarea"
      v-bind="$attrs"
      :disabled="inputDisabled"
      :readonly="readonly"
      :autocomplete="autoComplete || autocomplete"
      :style="textareaStyle"
      @focus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
      :aria-label="label"
    />
    <span
      v-if="isWordLimitVisible && type === 'textarea'"
      class="el-input__count"
    >{{ textLength }}/{{ upperLimit }}</span>
  </div>
</template>

<script>
import { Input } from 'element-ui';

export default {
  extends: Input,
  props: {
    autocomplete: {
      type: String,
      default: '',
    },
    hasArrow: {
      type: Boolean,
      default: false,
    },
    stateIcon: String,
  },
  computed: {
    stateIconSrc() {
      return this.stateIcon ? this.getStateIconSrc(this.stateIcon) : '';
    },
  },
  methods: {
    handleFocus(event) {
      this.focused = true;
      this.$emit('focus', event);

      this.dispatch('ElFormItem', 'el.form.input.focus');
    },
    handleBlur(event) {
      this.focused = false;
      this.$emit('blur', event);
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.blur', [this.value]);
      }
      this.dispatch('ElFormItem', 'el.form.input.blur');
    },
    checkImageExists(image, req) {
      try {
        return req.keys().includes(`./${image}.svg`);
      } catch (error) {
        return false;
      }
    },
    getStateIconSrc(image) {
      const req = require.context('~/images/input_state_icon', false, /\.svg$/);
      return this.checkImageExists(image, req) ? req(`./${image}.svg`) : '';
    },
  },
};
</script>

<style lang="scss">
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px #ffffff inset !important;
}

.el-input {
  &.is-disabled {
    .el-input__inner:disabled {
      font-weight: 600;
      color: $--color-text-regular;
      -webkit-text-fill-color: $--color-text-regular;
      cursor: default;
    }
  }
}

.el-input__inner {
  height: rem(36);
}

.el-textarea__inner,
.el-input__inner {
  padding: rem(8px);
  line-height: 1.4;

  &::placeholder {
    color: $--color-text-blue;
    font-weight: 400;
  }
}

.el-input--prefix {
  .el-input__inner {
    padding-left: rem(36px);

    &:focus {
      border-color: $--input-border-color;
    }
  }

  &.el-input--extra-large {
    .el-input__inner {
      padding-left: rem(70px);
    }
  }
}

.el-input__prefix {
  left: 0;
  width: rem(32px);
  height: 100%;
  border-right: 0;
  font-size: rem(16px);
  pointer-events: none;
  .el-input__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    pointer-events: none;
    color: $--color-lighter-blue-new;
    cursor: default;
  }
}

.el-input--large {
  .el-input__inner {
    height: $--input-large-height;
  }

  .el-input__prefix {
    width: rem(40px);
  }

  .el-input__icon {
    line-height: $--input-large-height;
  }
}

.floating-label {
  .el-input--extra-small,
  .el-select--extra-small {
    font-size: rem(14px);

    .el-input__inner {
      padding-top: 16px;
      padding-bottom: 16px;
    }

    & + .el-input__label {
      font-size: rem(12px);
      top: -12px;
    }
  }

  .el-input--small,
  .el-select--small {
    font-size: rem(16px);

    .el-input__inner {
      padding-top: 19px;
      padding-bottom: 19px;
    }

    & + .el-input__label {
      top: -14px;
    }
  }

  .el-input--large {
    font-size: rem(16px);

    .el-input__inner {
      height: rem(56px);
    }

    .el-input__prefix {
      width: rem(56px);
    }

    .el-input__icon {
      line-height: rem(56px);
    }

    &.el-input--suffix .el-input__inner {
      padding-right: 30px;
    }
  }

  .el-input:not(.is-focused) {
    .el-input--extra-small + .el-input__label,
    .autocomplete--extra-small + .el-input__label {
      font-size: rem(14px);
      top: 9px;
    }

    .el-input--small + .el-input__label,
    .autocomplete--small + .el-input__label {
      top: 11px;
    }

    .el-input--large + .el-input__label,
    .autocomplete--large + .el-input__label {
      font-size: rem(16px);
      top: 18px;
    }
  }
}

.el-input--extra-large {
  .el-input__inner {
    height: rem(55px);
    font-size: rem(22px);
    font-weight: 600;

    &::placeholder {
      font-size: rem(16px);
    }
  }

  .el-input__prefix {
    width: rem(55px);
    font-size: rem(22px);
    border-right: 1px solid $--color-lighter-blue-new;
  }

  .el-input__suffix {
    font-size: rem(22px);
  }

  .el-input__icon {
    line-height: rem(55px);
  }
}

.el-input--with-state-icon {
  .el-input__inner {
    padding-right: 40px;
  }

  .el-input__suffix {
    right: 12px;
    z-index: 2;

    img {
      height: auto;
      transform-origin: center;
      width: 24px;
      will-change: transform, opacity;
    }
  }

  .el-input__suffix-inner {
    display: flex;
    align-items: center;
    height: 100%;
  }

  &.el-input--large {
    .el-input__suffix {
      width: auto;
    }
  }

  .input-state-icon.sync {
    transform-origin: calc(50% - 1px) 50%;
    animation: rotate .4s ease-in infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}

.el-input--with-arrow {
  .el-input__inner {
    padding-right: 40px;
  }

  .el-input__arrow {
    color: $--color-primary;
    font-size: 24px;
    height: 24px;
    margin-left: 8px;
    transition: transform .3s;
    transform: rotateZ(180deg);
  }

  &.is-active {
    .el-input__arrow {
      transform: rotateZ(0deg);
    }
  }

  &.el-input--with-state-icon {
    .el-input__inner {
      padding-right: 84px;
    }
  }
}

.el-input__label:not(.is-floating) {
  .el-input:has(.el-input--with-state-icon) & {
    max-width: calc(100% - 42px);
  }

  .el-input:has(.el-input--with-state-icon.el-input--with-arrow) & {
    max-width: calc(100% - 80px);
  }
}
</style>

<style lang="scss" scoped>
.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: all .1s ease;
}

.icon-fade-enter {
  opacity: 0;
  transform: scale(.8) rotate(-45deg);
}

.icon-fade-leave-to {
  opacity: 0;
  transform: scale(.8) rotate(45deg);
}

.el-input__suffix-inner--no-events {
  pointer-events: none;
}
</style>
